import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfMonth,
  subMonths,
} from "date-fns"
import { ko } from "date-fns/locale"
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeftRight,
  Circle,
  Folder,
  FolderOpen,
  Maximize2,
  Plus,
  FileText,
  LayoutGrid,
} from "lucide-react"
import WorkReportPanel from "./WorkReportPanel"
import CursorOverlay from "./CursorOverlay"
import { useSocket } from "../../context/SocketContext"
import { buildHierarchyRows } from "./wbsHierarchy"
import { groupAgentJournalTasks } from "./agentJournalGrouping"

const DAY_WIDTH = 32
const ROW_HEIGHT = 34
const TABLE_WIDTH = 610

const STATUS = {
  done: { label: "완료", color: "#16a34a", bg: "#dcfce7" },
  delayed: { label: "지연", color: "#ef4444", bg: "#fee2e2" },
  progress: { label: "진행중", color: "#2563eb", bg: "#dbeafe" },
  planned: { label: "예정", color: "#9ca3af", bg: "#f3f4f6" },
}

const typeLabels = {
  planning: "기획",
  design: "디자인",
  development: "개발",
  testing: "테스트",
  deployment: "배포",
}

const typeOrder = ["planning", "design", "development", "testing", "deployment"]

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f8fafc;
  color: #0f172a;
`

const TopBar = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 0 18px;
  background: white;
  border-bottom: 1px solid #dbe2ea;
  flex-shrink: 0;

  @media (max-width: 900px) {
    height: auto;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 10px;
  }
`

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 26px;
  min-width: 0;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0;
  white-space: nowrap;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
  white-space: nowrap;
`

const MetaStrong = styled.span`
  color: #334155;
  font-weight: 700;
`

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 5px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
`

const ViewSegment = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
`

const ViewSegmentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  background: ${(p) => (p.active ? "white" : "transparent")};
  color: ${(p) => (p.active ? "#0f172a" : "#64748b")};
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 600 : 500)};
  border-radius: 6px;
  cursor: pointer;
  box-shadow: ${(p) => (p.active ? "0 1px 2px rgba(0,0,0,0.05)" : "none")};
  transition: background 0.15s, color 0.15s;

  &:hover { color: #0f172a; }
`

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
`

const Swatch = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 2px;
  background: ${(props) => props.color};
`

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 34px;
  padding: 0 13px;
  border: 1px solid ${(props) => (props.primary ? "#2563eb" : "#dbe2ea")};
  border-radius: 5px;
  background: ${(props) => (props.primary ? "#2563eb" : "white")};
  color: ${(props) => (props.primary ? "white" : "#334155")};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: ${(props) => (props.primary ? "#1d4ed8" : "#f8fafc")};
  }
`

const Board = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: white;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`

const TablePane = styled.div`
  width: ${TABLE_WIDTH}px;
  flex-shrink: 0;
  border-right: 1px solid #dbe2ea;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: white;

  @media (max-width: 900px) {
    width: 100%;
    max-height: 45vh;
    overflow-x: auto;
    border-right: none;
    border-bottom: 2px solid #dbe2ea;
  }
`

const TimelinePane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;

  @media (max-width: 900px) {
    min-height: 50vh;
  }
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 78px 1.8fr 72px 82px 82px 68px 56px;
  height: 68px;
  border-bottom: 1px solid #dbe2ea;
  background: #f8fafc;
  flex-shrink: 0;
`

const Th = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-right: 1px solid #e5eaf0;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
`

const TableBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const TableRows = styled.div`
  transform: translateY(${(props) => -props.scrollTop}px);
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 78px 1.8fr 72px 82px 82px 68px 56px;
  height: ${ROW_HEIGHT}px;
  background: ${(props) => (props.group ? "#f1f6fd" : "white")};
  border-bottom: 1px solid #edf1f5;
`

const Cell = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-right: 1px solid #edf1f5;
  color: #334155;
  font-size: 12px;
  font-weight: ${(props) => (props.strong ? 800 : 600)};
  justify-content: ${(props) => props.align || "flex-start"};
`

const WbsCode = styled.span`
  color: #475569;
`

const NameCell = styled(Cell)`
  gap: 6px;
`

const ToggleButton = styled.button`
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  border-radius: 3px;
  background: white;
  color: #475569;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
`

const Indent = styled.span`
  width: ${(props) => props.level * 16}px;
  flex-shrink: 0;
`

const TaskName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProgressText = styled.span`
  color: ${(props) => (props.value >= 100 ? "#16a34a" : props.value === 0 ? "#64748b" : "#2563eb")};
  font-weight: 800;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  min-width: 42px;
  padding: 0 7px;
  border-radius: 4px;
  color: ${(props) => props.color};
  background: ${(props) => props.bg};
  font-size: 11px;
  font-weight: 800;
`

const MonthHeader = styled.div`
  height: 34px;
  display: flex;
  position: relative;
  flex-shrink: 0;
  background: #f8fafc;
  border-bottom: 1px solid #dbe2ea;
`

const MonthCell = styled.div`
  position: absolute;
  top: 0;
  height: 34px;
  display: flex;
  align-items: center;
  padding-left: 12px;
  border-right: 1px solid #dbe2ea;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
`

const DayHeader = styled.div`
  height: 34px;
  display: flex;
  position: relative;
  flex-shrink: 0;
  background: white;
  border-bottom: 1px solid #dbe2ea;
`

const DayCell = styled.div`
  position: absolute;
  top: 0;
  width: ${DAY_WIDTH}px;
  height: 34px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  border-right: 1px solid #edf1f5;
  color: ${(props) => (props.weekend ? (props.sunday ? "#ef4444" : "#2563eb") : "#475569")};
  background: ${(props) => (props.weekend ? "#fbfdff" : "white")};
  font-size: 11px;
  font-weight: 700;
`

const TimelineBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
`

const TimelineCanvas = styled.div`
  position: relative;
  min-height: 100%;
`

const Grid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`

const GridLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: ${DAY_WIDTH}px;
  border-right: 1px solid #edf1f5;
  background: ${(props) => (props.weekend ? "#fbfdff" : "transparent")};
`

const TimelineRow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: ${ROW_HEIGHT}px;
  border-bottom: 1px solid #edf1f5;
  background: ${(props) => (props.group ? "rgba(241, 246, 253, 0.72)" : "transparent")};
`

const Bar = styled.button`
  position: absolute;
  top: 8px;
  height: 16px;
  border: 0;
  border-radius: 2px;
  background: ${(props) => props.color};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.14);
  cursor: pointer;
  overflow: hidden;
  padding: 0;

  &:hover {
    filter: brightness(0.95);
  }
`

const BarProgress = styled.span`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(props) => props.width}%;
  background: rgba(255, 255, 255, 0.22);
`

const TodayLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ef4444;
  z-index: 4;
  pointer-events: none;

  &::before {
    content: "오늘";
    position: absolute;
    top: -16px;
    left: -16px;
    width: 32px;
    height: 14px;
    border-radius: 3px;
    background: #ef4444;
    color: white;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const RefLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #2563eb;
  z-index: 5;
  cursor: ew-resize;

  &::before {
    content: "기준일";
    position: absolute;
    top: -16px;
    left: -20px;
    width: 40px;
    height: 14px;
    border-radius: 3px;
    background: #2563eb;
    color: white;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ew-resize;
  }
`

const StatsBar = styled.div`
  display: flex;
  gap: 12px;
  padding: 8px 18px;
  background: #f1f5f9;
  border-bottom: 1px solid #dbe2ea;
  flex-shrink: 0;
  font-size: 12px;
`

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 14px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  min-width: 80px;
`

const StatLabel = styled.span`
  color: #64748b;
  font-size: 11px;
`

const StatValue = styled.span`
  color: ${(props) => props.color || "#0f172a"};
  font-size: 16px;
  font-weight: 700;
`

const EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
`

// 일정 기준 목표 진척률 자동 계산
const computeBaselineProgress = (startDate, endDate, refDate) => {
  if (!startDate || !endDate) return 0
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  const ref = refDate ? new Date(refDate).getTime() : Date.now()
  if (isNaN(start) || isNaN(end) || end <= start) return 0
  if (ref <= start) return 0
  if (ref >= end) return 100
  return Math.round(((ref - start) / (end - start)) * 100)
}

// 기준일로부터 남은 일수 (양수: 종료까지, 음수: 초과). progress 100이면 null
const remainingFromRef = (task, refDate) => {
  if (Number(task.progress || 0) >= 100) return { kind: "done" }
  const ref = refDate ? new Date(refDate) : new Date()
  const start = task.startDate ? new Date(task.startDate) : null
  const end = task.endDate ? new Date(task.endDate) : null
  if (!start || !end) return null
  // 자정 기준으로 통일
  const refDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate())
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  if (refDay < startDay) {
    return { kind: "toStart", days: differenceInCalendarDays(startDay, refDay) }
  }
  if (refDay > endDay) {
    return { kind: "overdue", days: differenceInCalendarDays(refDay, endDay) }
  }
  return { kind: "toEnd", days: differenceInCalendarDays(endDay, refDay) }
}

// 기준일 기반 상태 자동 계산
const getStatus = (task, refDate) => {
  const progress = Number(task.progress || 0)
  const ref = refDate ? new Date(refDate) : new Date()

  if (progress >= 100) return STATUS.done
  if (task.startDate && isAfter(new Date(task.startDate), ref)) return STATUS.planned
  // 기준일 기준 목표 진척률보다 실제가 낮으면 지연
  const baseline = computeBaselineProgress(task.startDate, task.endDate, ref)
  if (task.endDate && isBefore(new Date(task.endDate), ref) && progress < 100) return STATUS.delayed
  if (progress < baseline - 5) return STATUS.delayed
  return STATUS.progress
}

const normalizeDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const makeGroupSummary = (group, children) => {
  const dates = children.flatMap((task) => [normalizeDate(task.startDate), normalizeDate(task.endDate)]).filter(Boolean)
  const progress = children.length
    ? Math.round(children.reduce((sum, task) => sum + Number(task.progress || 0), 0) / children.length)
    : 0

  return {
    id: `group-${group}`,
    name: typeLabels[group] || group || "기타",
    type: group,
    startDate: dates.length ? new Date(Math.min(...dates.map((date) => date.getTime()))) : null,
    endDate: dates.length ? new Date(Math.max(...dates.map((date) => date.getTime()))) : null,
    progress,
    isGroup: true,
    children,
  }
}

export default function GanttChartWBS({
  tasks = [],
  users = [],
  projectName = "프로젝트",
  zoomLevel = 1,
  setZoomLevel,
  referenceDate,
  setReferenceDate,
  onTaskSelect = () => {},
  onAddTask,
  chartId,
  currentUserId,
}) {
  const bodyRef = useRef(null)
  const headerRef = useRef(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [expanded, setExpanded] = useState(() => new Set())
  const [visibleStart, setVisibleStart] = useState(() => startOfMonth(new Date()))
  const [activeView, setActiveView] = useState("wbs") // 'wbs' | 'reports'
  const [reportJumpTask, setReportJumpTask] = useState(null) // { taskId, date } - WBS에서 클릭한 작업으로 보고서 자동 시작
  const [taskReportMap, setTaskReportMap] = useState(new Map()) // Map<taskId, Array<{type, start, end}>>

  // 차트의 모든 보고서를 불러와 작업별 보고서 배열 구축
  const loadTaskReports = useCallback(async () => {
    if (!chartId) {
      setTaskReportMap(new Map())
      return
    }
    try {
      const res = await fetch(`/request/work-reports?chart_id=${chartId}`, { credentials: "include" })
      const data = await res.json()
      if (!data.success) return
      const map = new Map()
      ;(data.reports || []).forEach((r) => {
        const tid = r.linked_task_ids && r.linked_task_ids[0]
        if (!tid) return
        const key = String(tid)
        if (!map.has(key)) map.set(key, [])
        map.get(key).push({
          type: r.report_type,
          start: new Date(r.period_start),
          end: new Date(r.period_end),
        })
      })
      setTaskReportMap(map)
    } catch (e) {
      console.error("WBS 보고서 맵 조회 실패:", e)
    }
  }, [chartId])

  // 차트 변경/뷰 전환 시 보고서 맵 재조회 (reports 뷰에서 돌아올 때 최신화)
  useEffect(() => {
    if (activeView === "wbs") loadTaskReports()
  }, [activeView, loadTaskReports])

  // WBS에서 작업 클릭 시 보고서 작성 모드로 점프 (특정 날짜 override 가능)
  const handleTaskJump = useCallback((taskId, dateOverride) => {
    const task = tasks.find((t) => String(t.id) === String(taskId))
    if (!task) return
    const today = new Date()
    const start = task.startDate ? new Date(task.startDate) : today
    const end = task.endDate ? new Date(task.endDate) : today
    const inRange = today >= start && today <= end
    const targetDate = dateOverride || (inRange ? today : start)
    setActiveView("reports")
    setReportJumpTask({ taskId: String(taskId), date: targetDate, ts: Date.now() })
  }, [tasks])

  const dayWidth = Math.max(22, Math.round(DAY_WIDTH * zoomLevel))

  const groupedRows = useMemo(() => {
    if (!tasks.length) return []

    if (projectName === "에이전트 업무일지" && !tasks.some((task) => task.parentId)) {
      return groupAgentJournalTasks(tasks)
    }

    const hasHierarchy = tasks.some((task) => task.parentId)
    if (hasHierarchy) return buildHierarchyRows(tasks)

    const groups = new Map()
    tasks.forEach((task) => {
      const type = task.type || "기타"
      if (!groups.has(type)) groups.set(type, [])
      groups.get(type).push(task)
    })

    return [...groups.entries()]
      .sort(([a], [b]) => {
        const ai = typeOrder.indexOf(a)
        const bi = typeOrder.indexOf(b)
        if (ai === -1 && bi === -1) return String(a).localeCompare(String(b), "ko")
        if (ai === -1) return 1
        if (bi === -1) return -1
        return ai - bi
      })
      .map(([group, children]) => makeGroupSummary(group, children))
  }, [tasks, projectName])

  useEffect(() => {
    if (projectName !== "에이전트 업무일지") {
      setExpanded(new Set(groupedRows.map((task) => task.id)))
      return
    }
    setExpanded((previous) => {
      // 묶음은 분야·월·일로 깊어진다. 두 단만 모으면 펼친 날짜가 다음 렌더에서
      // 사라진 id 로 취급돼 곧바로 다시 접힌다.
      const available = new Set()
      const collect = (items) => items.forEach((item) => {
        if (!item.children?.length) return
        available.add(item.id)
        collect(item.children)
      })
      collect(groupedRows)
      return new Set([...previous].filter((id) => available.has(id)))
    })
  }, [groupedRows, projectName])

  const rows = useMemo(() => {
    const result = []
    const append = (items, level = 0, prefix = "") => {
      items.forEach((item, index) => {
        const code = prefix ? `${prefix}.${index + 1}` : `${index + 1}`
        result.push({ ...item, level, code })
        if (item.children?.length && expanded.has(item.id)) {
          append(item.children, level + 1, code)
        }
      })
    }
    append(groupedRows)
    return result
  }, [expanded, groupedRows])

  const projectEndForRange = useMemo(() => {
    const datesFromTasks = tasks.flatMap((task) => [normalizeDate(task.endDate)]).filter(Boolean)
    if (!datesFromTasks.length) return null
    return new Date(Math.max(...datesFromTasks.map((date) => date.getTime())))
  }, [tasks])

  const visibleEnd = useMemo(() => {
    const defaultEnd = endOfMonth(addMonths(visibleStart, 1))
    if (projectEndForRange) {
      const projectEnd = endOfMonth(projectEndForRange)
      return isAfter(projectEnd, defaultEnd) ? projectEnd : defaultEnd
    }
    return defaultEnd
  }, [visibleStart, projectEndForRange])
  const dates = useMemo(() => eachDayOfInterval({ start: visibleStart, end: visibleEnd }), [visibleStart, visibleEnd])
  const totalWidth = dates.length * dayWidth
  const totalHeight = Math.max(rows.length * ROW_HEIGHT, 1)

  const monthGroups = useMemo(() => {
    const groups = []
    let cursor = startOfMonth(visibleStart)
    while (cursor <= visibleEnd) {
      const monthStart = isBefore(cursor, visibleStart) ? visibleStart : cursor
      const monthEnd = isAfter(endOfMonth(cursor), visibleEnd) ? visibleEnd : endOfMonth(cursor)
      const left = differenceInCalendarDays(monthStart, visibleStart) * dayWidth
      const width = (differenceInCalendarDays(monthEnd, monthStart) + 1) * dayWidth
      groups.push({ date: cursor, left, width })
      cursor = addMonths(cursor, 1)
    }
    return groups
  }, [dayWidth, visibleEnd, visibleStart])

  const projectRange = useMemo(() => {
    const datesFromTasks = tasks.flatMap((task) => [normalizeDate(task.startDate), normalizeDate(task.endDate)]).filter(Boolean)
    if (!datesFromTasks.length) return null
    const start = new Date(Math.min(...datesFromTasks.map((date) => date.getTime())))
    const end = new Date(Math.max(...datesFromTasks.map((date) => date.getTime())))
    return { start, end }
  }, [tasks])

  useEffect(() => {
    if (projectRange) setVisibleStart(startOfMonth(projectRange.start))
  }, [projectRange])

  const handleScroll = useCallback((event) => {
    setScrollTop(event.currentTarget.scrollTop)
    if (headerRef.current) headerRef.current.scrollLeft = event.currentTarget.scrollLeft
  }, [])

  // 타임라인 위 마우스 움직임 → 소켓 전송 (팀 차트일 때만)
  // x는 줌/표시 시작 월이 달라도 동일하게 보이도록 날짜(UTC 일수, 소수)로 정규화해 전송
  const { emitCursorMove, currentChartId } = useSocket()
  const dayNumOf = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000
  const canvasRef = useRef(null)
  const handleTimelineCursorMove = useCallback((e) => {
    if (!currentChartId || !canvasRef.current) return
    // 좌표는 TimelineCanvas(콘텐츠) 요소 기준으로 직접 측정 — 스크롤 오프셋 무관
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const day = dayNumOf(visibleStart) + x / dayWidth
    // y는 행 ID + 행 내 오프셋으로 보냄 — 그룹 접힘 상태가 서로 달라도 같은 작업 행 위에 표시
    const rowIndex = Math.floor(y / ROW_HEIGHT)
    const row = rows[rowIndex]
    emitCursorMove({
      day,
      y,
      rowId: row ? row.id : null,
      oy: row ? y - rowIndex * ROW_HEIGHT : null,
      view: 'wbs',
      // fx/fy: 수신 측이 다른 뷰라 정밀 매핑이 불가능할 때 쓰는 화면 비율 폴백
      fx: e.clientX / window.innerWidth,
      fy: e.clientY / window.innerHeight,
    })
  }, [currentChartId, emitCursorMove, visibleStart, dayWidth, rows]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const getUserName = (assignee) => {
    const user = users.find((item) => item.id === assignee)
    return user?.name || assignee || "-"
  }

  const getBarGeometry = (task) => {
    const start = normalizeDate(task.startDate)
    const end = normalizeDate(task.endDate)
    if (!start || !end) return null

    const clampedStart = isBefore(start, visibleStart) ? visibleStart : start
    const clampedEnd = isAfter(end, visibleEnd) ? visibleEnd : end
    if (isAfter(clampedStart, visibleEnd) || isBefore(clampedEnd, visibleStart)) return null

    const left = differenceInCalendarDays(clampedStart, visibleStart) * dayWidth
    const width = Math.max((differenceInCalendarDays(clampedEnd, clampedStart) + 1) * dayWidth - 4, 10)
    return { left: left + 2, width }
  }

  const todayOffset = differenceInCalendarDays(new Date(), visibleStart)
  const showToday = todayOffset >= 0 && todayOffset < dates.length

  const refDateObj = referenceDate ? new Date(referenceDate) : new Date()
  const refOffset = differenceInCalendarDays(refDateObj, visibleStart)
  const showRef = refOffset >= 0 && refOffset < dates.length

  // 진척률 요약
  const summary = useMemo(() => {
    const leaves = tasks.filter((t) => !t.children?.length)
    const total = leaves.length || tasks.length
    if (!total) return { total: 0, avgActual: 0, avgBaseline: 0, done: 0, delayed: 0, progress: 0, planned: 0 }
    const list = leaves.length ? leaves : tasks
    let actualSum = 0, baselineSum = 0
    let done = 0, delayed = 0, prog = 0, planned = 0
    list.forEach((t) => {
      const p = Number(t.progress || 0)
      const b = computeBaselineProgress(t.startDate, t.endDate, refDateObj)
      actualSum += p
      baselineSum += b
      const s = getStatus(t, refDateObj)
      if (s === STATUS.done) done++
      else if (s === STATUS.delayed) delayed++
      else if (s === STATUS.planned) planned++
      else prog++
    })
    return {
      total: list.length,
      avgActual: Math.round(actualSum / list.length),
      avgBaseline: Math.round(baselineSum / list.length),
      done, delayed, progress: prog, planned,
    }
  }, [tasks, refDateObj])

  // Fit to view: 컨테이너 폭에 프로젝트 전체 일수 맞추도록 zoom 계산
  const fitToView = useCallback(() => {
    if (!projectRange || !setZoomLevel || !bodyRef.current) return
    const totalDays = differenceInCalendarDays(visibleEnd, visibleStart) + 1
    if (totalDays <= 0) return
    const containerWidth = bodyRef.current.clientWidth - 20
    const needPerDay = containerWidth / totalDays
    const newZoom = Math.max(0.1, Math.min(5, needPerDay / DAY_WIDTH))
    setZoomLevel(Number(newZoom.toFixed(2)))
  }, [projectRange, setZoomLevel, visibleEnd, visibleStart])

  // 휠 줌 (Ctrl/Cmd + Wheel)
  const handleWheel = useCallback((e) => {
    if (!setZoomLevel) return
    if (!(e.ctrlKey || e.metaKey)) return
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setZoomLevel((z) => Math.max(0.1, Math.min(5, Number((z + delta).toFixed(2)))))
  }, [setZoomLevel])

  // 기준일 세로선 드래그
  const dragStateRef = useRef(null)
  const handleRefDragStart = (e) => {
    if (!setReferenceDate) return
    e.preventDefault()
    dragStateRef.current = { active: true }
    const onMove = (ev) => {
      if (!dragStateRef.current?.active || !bodyRef.current) return
      const rect = bodyRef.current.getBoundingClientRect()
      const x = ev.clientX - rect.left + bodyRef.current.scrollLeft
      const dayIndex = Math.floor(x / dayWidth)
      const clamped = Math.max(0, Math.min(dates.length - 1, dayIndex))
      const newDate = dates[clamped]
      if (newDate) setReferenceDate(newDate)
    }
    const onUp = () => {
      dragStateRef.current = null
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }

  return (
    <Shell>
      <TopBar data-cursor-anchor="wbs-topbar">
        <TopLeft>
          <Title>{activeView === "reports" ? "업무 보고서" : "프로젝트 WBS"}</Title>
          <ViewSegment>
            <ViewSegmentButton active={activeView === "wbs"} onClick={() => setActiveView("wbs")}>
              <LayoutGrid size={14} /> WBS
            </ViewSegmentButton>
            <ViewSegmentButton active={activeView === "reports"} onClick={() => setActiveView("reports")}>
              <FileText size={14} /> 보고서
            </ViewSegmentButton>
          </ViewSegment>
          {activeView === "wbs" && (
            <>
              <Pill>
                <Circle size={8} fill="#2563eb" />
                진행중
              </Pill>
              <Meta>
                프로젝트명:
                <MetaStrong>{projectName}</MetaStrong>
              </Meta>
              <Meta>
                기간:
                <MetaStrong>
                  {projectRange
                    ? `${format(projectRange.start, "yyyy.MM.dd")} ~ ${format(projectRange.end, "yyyy.MM.dd")}`
                    : "작업 기간 없음"}
                </MetaStrong>
                <CalendarDays size={15} />
              </Meta>
            </>
          )}
        </TopLeft>

        <TopRight>
          {activeView === "wbs" && (
            <>
              <Legend>
                <LegendItem>
                  <Swatch color={STATUS.progress.color} />
                  진행중
                </LegendItem>
                <LegendItem>
                  <Swatch color={STATUS.done.color} />
                  완료
                </LegendItem>
                <LegendItem>
                  <Swatch color={STATUS.delayed.color} />
                  지연
                </LegendItem>
                <LegendItem>
                  <Swatch color={STATUS.planned.color} />
                  예정
                </LegendItem>
              </Legend>
              <ActionButton onClick={() => setVisibleStart((date) => subMonths(date, 1))} title="이전 달">
                <ChevronLeft size={16} />
              </ActionButton>
              <ActionButton onClick={() => setVisibleStart((date) => addMonths(date, 1))} title="다음 달">
                <ChevronRight size={16} />
              </ActionButton>
              {onAddTask && (
                <ActionButton primary onClick={onAddTask}>
                  <Plus size={16} />
                  작업 추가
                </ActionButton>
              )}
            </>
          )}
        </TopRight>
      </TopBar>

      {activeView === "reports" ? (
        <WorkReportPanel
          chartId={chartId}
          tasks={tasks}
          users={users}
          currentUserId={currentUserId}
          jumpTask={reportJumpTask}
          onJumpHandled={() => setReportJumpTask(null)}
        />
      ) : (
      <>
      <StatsBar>
        <StatCard>
          <StatLabel>전체 작업</StatLabel>
          <StatValue>{summary.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>실제 평균</StatLabel>
          <StatValue color="#2563eb">{summary.avgActual}%</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>목표 평균</StatLabel>
          <StatValue color="#64748b">{summary.avgBaseline}%</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>완료</StatLabel>
          <StatValue color={STATUS.done.color}>{summary.done}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>진행중</StatLabel>
          <StatValue color={STATUS.progress.color}>{summary.progress}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>지연</StatLabel>
          <StatValue color={STATUS.delayed.color}>{summary.delayed}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>예정</StatLabel>
          <StatValue color={STATUS.planned.color}>{summary.planned}</StatValue>
        </StatCard>
      </StatsBar>

      <Board>
        <TablePane data-cursor-anchor="wbs-table">
          <TableHeader>
            <Th>WBS 코드</Th>
            <Th>작업명</Th>
            <Th>담당자</Th>
            <Th>시작일</Th>
            <Th>종료일</Th>
            <Th>남은일</Th>
            <Th>진척률(%)</Th>
            <Th>상태</Th>
          </TableHeader>

          <TableBody>
            {rows.length ? (
              <TableRows scrollTop={scrollTop}>
                {rows.map((task) => {
                  const status = getStatus(task, refDateObj)
                  return (
                    <Row
                      key={task.id}
                      group={task.isGroup}
                      data-cursor-anchor={`wbs-row-${task.id}`}
                      onClick={() => { if (task.isGroup) toggle(task.id); else handleTaskJump(task.id) }}
                      style={{ cursor: "pointer" }}
                      title={!task.isGroup ? `${task.name} - 클릭하여 보고서 작성` : ""}
                    >
                      <Cell align="center" strong={task.isGroup}>
                        <WbsCode>{task.code}</WbsCode>
                      </Cell>
                      <NameCell strong={task.isGroup}>
                        <Indent level={task.level} />
                        {task.children?.length ? (
                          <ToggleButton onClick={(e) => { e.stopPropagation(); toggle(task.id) }}>
                            {expanded.has(task.id) ? <FolderOpen size={13} /> : <Folder size={13} />}
                          </ToggleButton>
                        ) : (
                          <ChevronsLeftRight size={14} color="#cbd5e1" />
                        )}
                        <TaskName>{task.name}</TaskName>
                      </NameCell>
                      <Cell align="center">{task.isGroup ? "-" : getUserName(task.assignee)}</Cell>
                      <Cell align="center">{task.startDate ? format(normalizeDate(task.startDate), "yyyy-MM-dd") : "-"}</Cell>
                      <Cell align="center">{task.endDate ? format(normalizeDate(task.endDate), "yyyy-MM-dd") : "-"}</Cell>
                      <Cell align="center">
                        {(() => {
                          if (task.isGroup) return "-"
                          const r = remainingFromRef(task, refDateObj)
                          if (!r) return "-"
                          if (r.kind === "done") return <span style={{ color: "#16a34a", fontWeight: 600 }}>완료</span>
                          if (r.kind === "toStart") return <span style={{ color: "#9ca3af" }}>D-{r.days}</span>
                          if (r.kind === "overdue") return <span style={{ color: "#ef4444", fontWeight: 600 }}>+{r.days}일</span>
                          return <span style={{ color: r.days <= 3 ? "#ef4444" : r.days <= 7 ? "#f59e0b" : "#0f172a", fontWeight: 600 }}>D-{r.days}</span>
                        })()}
                      </Cell>
                      <Cell align="center">
                        <ProgressText value={Number(task.progress || 0)}>{task.progress || 0}%</ProgressText>
                      </Cell>
                      <Cell align="center">
                        <StatusBadge color={status.color} bg={status.bg}>
                          {status.label}
                        </StatusBadge>
                      </Cell>
                    </Row>
                  )
                })}
              </TableRows>
            ) : (
              <EmptyState>작업을 추가하면 WBS 타임라인이 표시됩니다.</EmptyState>
            )}
          </TableBody>
        </TablePane>

        <TimelinePane>
          <div ref={headerRef} style={{ overflow: "hidden", flexShrink: 0 }}>
            <div style={{ width: totalWidth }}>
              <MonthHeader>
                {monthGroups.map((month) => (
                  <MonthCell key={month.date.toISOString()} style={{ left: month.left, width: month.width }}>
                    {format(month.date, "yyyy년 M월", { locale: ko })}
                  </MonthCell>
                ))}
              </MonthHeader>
              <DayHeader>
                {dates.map((date, index) => {
                  const sunday = date.getDay() === 0
                  const weekend = sunday || date.getDay() === 6
                  return (
                    <DayCell
                      key={date.toISOString()}
                      weekend={weekend}
                      sunday={sunday}
                      style={{ left: index * dayWidth, width: dayWidth }}
                    >
                      <span>{format(date, "dd")}</span>
                      <span>{format(date, "EEEEE", { locale: ko })}</span>
                    </DayCell>
                  )
                })}
              </DayHeader>
            </div>
          </div>

          <TimelineBody ref={bodyRef} onScroll={handleScroll} onWheel={handleWheel} onMouseMove={handleTimelineCursorMove} data-cursor-zone="timeline">
            <TimelineCanvas ref={canvasRef} style={{ width: totalWidth, height: totalHeight }}>
              <Grid>
                {dates.map((date, index) => {
                  const weekend = date.getDay() === 0 || date.getDay() === 6
                  return <GridLine key={date.toISOString()} weekend={weekend} style={{ left: index * dayWidth, width: dayWidth }} />
                })}
              </Grid>

              {/* 의존선 */}
              <svg
                style={{
                  position: "absolute",
                  inset: 0,
                  width: totalWidth,
                  height: totalHeight,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              >
                <defs>
                  <marker id="wbs-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#475569" />
                  </marker>
                </defs>
                {rows.flatMap((task, toIdx) =>
                  (task.dependencies || []).map((depId) => {
                    const fromIdx = rows.findIndex((r) => r.id === depId)
                    if (fromIdx < 0) return null
                    const fromGeom = getBarGeometry(rows[fromIdx])
                    const toGeom = getBarGeometry(task)
                    if (!fromGeom || !toGeom) return null
                    const fx = fromGeom.left + fromGeom.width
                    const fy = fromIdx * ROW_HEIGHT + ROW_HEIGHT / 2
                    const tx = toGeom.left
                    const ty = toIdx * ROW_HEIGHT + ROW_HEIGHT / 2
                    const midX = Math.max(fx + 12, tx - 12)
                    return (
                      <path
                        key={`${depId}-${task.id}`}
                        d={`M ${fx} ${fy} L ${midX} ${fy} L ${midX} ${ty} L ${tx} ${ty}`}
                        fill="none"
                        stroke="#475569"
                        strokeWidth="1.4"
                        strokeDasharray="4 3"
                        markerEnd="url(#wbs-arrow)"
                      />
                    )
                  })
                )}
              </svg>

              {rows.map((task, index) => {
                const geometry = getBarGeometry(task)
                const status = getStatus(task, refDateObj)
                const isTodayRow = task.startDate && isSameDay(normalizeDate(task.startDate), new Date())
                const taskReports = !task.isGroup ? (taskReportMap.get(String(task.id)) || []) : []

                // 가시 범위 내 보고서만 필터
                const visibleReports = taskReports.filter(
                  (r) => r.end >= visibleStart && r.start <= visibleEnd
                )

                return (
                  <TimelineRow key={task.id} group={task.isGroup} style={{ top: index * ROW_HEIGHT }}>
                    {geometry && (
                      <Bar
                        color={task.isGroup ? "#64748b" : status.color}
                        style={{
                          left: geometry.left,
                          width: geometry.width,
                          height: task.isGroup ? 12 : 16,
                          top: task.isGroup ? 10 : 8,
                        }}
                        onClick={() => {
                          if (!task.isGroup) handleTaskJump(task.id)
                          else toggle(task.id)
                        }}
                        title={task.isGroup ? task.name : `${task.name} - 클릭하여 보고서 작성`}
                      >
                        <BarProgress width={Number(task.progress || 0)} />
                      </Bar>
                    )}

                    {/* 일간 보고서 - 동그란 점 */}
                    {visibleReports.filter((r) => r.type === "daily").map((r, i) => {
                      const dayIndex = differenceInCalendarDays(r.start, visibleStart)
                      if (dayIndex < 0 || dayIndex >= dates.length) return null
                      const left = dayIndex * dayWidth + dayWidth / 2 - 4
                      const key = `${r.start.getFullYear()}-${String(r.start.getMonth() + 1).padStart(2, "0")}-${String(r.start.getDate()).padStart(2, "0")}`
                      return (
                        <span
                          key={`rd-${task.id}-${i}`}
                          onClick={(e) => { e.stopPropagation(); handleTaskJump(task.id, r.start) }}
                          title={`일일 보고서 · ${key}`}
                          style={{
                            position: "absolute",
                            left,
                            top: 2,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#f59e0b",
                            boxShadow: "0 0 0 2px white, 0 1px 3px rgba(0,0,0,0.2)",
                            cursor: "pointer",
                            zIndex: 6,
                          }}
                        />
                      )
                    })}

                    {/* 주간 보고서 - 작업 바 위쪽 얇은 막대 */}
                    {visibleReports.filter((r) => r.type === "weekly").map((r, i) => {
                      const clampedStart = r.start < visibleStart ? visibleStart : r.start
                      const clampedEnd = r.end > visibleEnd ? visibleEnd : r.end
                      const startIdx = differenceInCalendarDays(clampedStart, visibleStart)
                      const days = differenceInCalendarDays(clampedEnd, clampedStart) + 1
                      const left = startIdx * dayWidth + 1
                      const width = Math.max(days * dayWidth - 2, 4)
                      return (
                        <span
                          key={`rw-${task.id}-${i}`}
                          onClick={(e) => { e.stopPropagation(); handleTaskJump(task.id, r.start) }}
                          title={`주간 보고서 · ${format(r.start, "MM.dd")} ~ ${format(r.end, "MM.dd")}`}
                          style={{
                            position: "absolute",
                            left,
                            top: 2,
                            width,
                            height: 4,
                            borderRadius: 2,
                            background: "#3b82f6",
                            boxShadow: "0 0 0 1px white",
                            cursor: "pointer",
                            zIndex: 5,
                          }}
                        />
                      )
                    })}

                    {/* 월간 보고서 - 작업 바 아래쪽 얇은 막대 */}
                    {visibleReports.filter((r) => r.type === "monthly").map((r, i) => {
                      const clampedStart = r.start < visibleStart ? visibleStart : r.start
                      const clampedEnd = r.end > visibleEnd ? visibleEnd : r.end
                      const startIdx = differenceInCalendarDays(clampedStart, visibleStart)
                      const days = differenceInCalendarDays(clampedEnd, clampedStart) + 1
                      const left = startIdx * dayWidth + 1
                      const width = Math.max(days * dayWidth - 2, 4)
                      return (
                        <span
                          key={`rm-${task.id}-${i}`}
                          onClick={(e) => { e.stopPropagation(); handleTaskJump(task.id, r.start) }}
                          title={`월간 보고서 · ${format(r.start, "yyyy.MM")}`}
                          style={{
                            position: "absolute",
                            left,
                            top: ROW_HEIGHT - 8,
                            width,
                            height: 4,
                            borderRadius: 2,
                            background: "#a855f7",
                            boxShadow: "0 0 0 1px white",
                            cursor: "pointer",
                            zIndex: 5,
                          }}
                        />
                      )
                    })}

                    {isTodayRow && <Check size={14} style={{ position: "absolute", left: 4, top: 10, color: "#2563eb" }} />}
                  </TimelineRow>
                )
              })}

              {showToday && <TodayLine style={{ left: todayOffset * dayWidth + dayWidth / 2 }} />}
              {showRef && (
                <RefLine
                  style={{ left: refOffset * dayWidth + dayWidth / 2 }}
                  onMouseDown={handleRefDragStart}
                  title="드래그로 기준일 변경"
                />
              )}

              {/* 원격 커서 오버레이 (팀 차트 실시간 협업) */}
              <CursorOverlay
                view="wbs"
                dayToX={(day) => (day - dayNumOf(visibleStart)) * dayWidth}
                rowToY={(rowId, oy) => {
                  const idx = rows.findIndex((r) => String(r.id) === String(rowId))
                  return idx < 0 ? null : idx * ROW_HEIGHT + oy
                }}
                scrollRef={bodyRef}
              />
            </TimelineCanvas>
          </TimelineBody>
        </TimelinePane>
      </Board>
      </>
      )}
    </Shell>
  )
}
